import DOMPurify from "isomorphic-dompurify"
import PropTypes from "prop-types"
import React, { useState } from "react"
import styles from "@/components/Shared/index.module.scss"
import useIsMobile from "@/hooks/useIsMobile"
import { PURE_TEXT_REGEX } from "@/constants/regex"

const MainFooter = props => {
  const { data, columnClassNames } = props
  const [gbhExpand, setGbhExpand] = useState([])
  const isMobile = useIsMobile(1024)
  const itemsOne = data?.itemsOne || []
  const itemsTwo = data?.itemsTwo || []
  const itemsThree = data?.itemsThree || []
  const items = data?.items || []
  const quadrantTitleThree = data?.quadrantTitleThree || ""

  const isExpanded = key => gbhExpand.includes(key)

  const getExpandedClassname = key => (isExpanded(key) ? "gbh-expand" : "")

  const handleGbhExpand = key => {
    if (!isExpanded(key)) {
      const newGbhExpand = [...gbhExpand]
      newGbhExpand.push(key)
      setGbhExpand(newGbhExpand)
    } else {
      const newGbhExpand = [...gbhExpand]
      const idx = gbhExpand.indexOf(key)
      newGbhExpand.splice(idx, 1)
      setGbhExpand(newGbhExpand)
    }
  }

  const stripHtml = html => {
    return html ? html.replace(PURE_TEXT_REGEX, "") : ""
  }

  const getLinksVisibilityClass = key => {
    return isExpanded(key) ? "" : "footer-links-hidden"
  }

  const renderLinkHeading = (key, titleHtml) => {
    if (!isMobile) {
      return (
        <h2
          className="footer-inner-head"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(titleHtml || ""),
          }}
        />
      )
    }

    return (
      <h2>
        <button
          type="button"
          className={`footer-inner-head ${getExpandedClassname(key)}`}
          onClick={() => handleGbhExpand(key)}
          aria-expanded={isExpanded(key)}
          aria-controls={`footer-inner-links-${key}`}
          aria-label={
            stripHtml(DOMPurify.sanitize(titleHtml || "")) || undefined
          }
        >
          <span
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(titleHtml || ""),
            }}
          />
          <span className="accordion" aria-hidden="true">
            <span className="line line-1"></span>
            <span className="line line-2"></span>
          </span>
        </button>
      </h2>
    )
  }

  return (
    <div
      className={`${columnClassNames ? columnClassNames : ""} ${
        styles.mainFooterWrapper
      }`}
    >
      <footer>
        <section
          className="gbh-footer-main-nav main-footer"
          aria-label="Footer"
        >
          <section className="gbh-container">
            <div className="row">
              <div className="col">
                {data?.radioNavigationOne === "descriptionOne" && (
                  <>
                    <h2>{data?.quadrantTitleOne}</h2>
                    <div
                      className="gbh-find-store"
                      role="listitem"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(data?.quadrantDescOne),
                      }}
                    />
                  </>
                )}
                {data?.radioNavigationOne === "linksOne" && (
                  <>
                    <div className="gbh-footer-main-nav-link">
                      {renderLinkHeading("linksOne", data?.quadrantTitleOne)}
                      <ul
                        className={getLinksVisibilityClass("linksOne")}
                        id={`footer-inner-links-linksOne`}
                      >
                        {items.map(item => {
                          const brandLinkOneHref = item?.brandLinkOne
                            ?.split(process.env.NEXT_PUBLIC_INTERNALLINKPATH)
                            .join("")
                            .replace(".html", "")
                          const linkOneKey = `${item?.brandLinkOne || ""}-${
                            item?.brandNameOne || ""
                          }`

                          return (
                            <React.Fragment key={linkOneKey}>
                              <li>
                                <a
                                  className="gbh-data-layer"
                                  href={brandLinkOneHref}
                                  target={item?.newTabOne ? "_blank" : "_self"}
                                  rel={
                                    item?.newTabOne ? "noreferrer" : undefined
                                  }
                                  aria-label={`${item?.brandNameOne}${
                                    item?.newTabOne
                                      ? " external link opens in a new tab"
                                      : ""
                                  }`}
                                  data-gbh-data-layer={`{"clickInternalLinks":"${
                                    item?.newTabOne ? "false" : "true"
                                  }","internalLinkPosition":"${
                                    data?.quadrantTitleOne?.toLowerCase() || ""
                                  }","internalLinkZoneName":"global footer","internalLinkURL":"${
                                    brandLinkOneHref || "n/a"
                                  }","internalLinkName":"${item?.brandNameOne?.toLowerCase()}","eventName":"footer menu:${item?.brandNameOne?.toLowerCase()}","internalLinkType":"global footer:navigation","eventType":"footer submenu","eventAction":"footer menu:${item?.brandNameOne?.toLowerCase()}"}`}
                                >
                                  {item?.brandNameOne}
                                </a>
                              </li>
                            </React.Fragment>
                          )
                        })}
                      </ul>
                    </div>
                  </>
                )}
              </div>
              <div className="col" aria-label={data?.quadrantTitleTwo}>
                {data?.radioNavigationTwo === "descriptionTwo" && (
                  <>
                    <div
                      className="footer-inner-head"
                      role="heading"
                      aria-level="2"
                    >
                      {data?.quadrantTitleTwo}
                    </div>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(data?.quadrantDescTwo),
                      }}
                    />
                  </>
                )}
                {data.radioNavigationTwo === "linksTwo" && (
                  <>
                    <div className="gbh-footer-main-nav-link">
                      {renderLinkHeading("linksTwo", data?.quadrantTitleTwo)}
                      <ul
                        className={`${getLinksVisibilityClass("linksTwo")} ${
                          itemsOne.length > 4 ? "odd-even" : ""
                        }`}
                        id="footer-inner-links-linksTwo"
                      >
                        {itemsOne.map(item => {
                          const brandLinkTwoHref = item?.brandLinkTwo
                            ?.split(process.env.NEXT_PUBLIC_INTERNALLINKPATH)
                            .join("")
                            .replace(".html", "")
                          const linkTwoKey = `${item?.brandLinkTwo || ""}-${
                            item?.brandNameTwo || ""
                          }`

                          return (
                            <li key={linkTwoKey}>
                              <a
                                className="gbh-data-layer"
                                href={brandLinkTwoHref}
                                target={item?.newTabTwo ? "_blank" : "_self"}
                                rel={item?.newTabTwo ? "noreferrer" : undefined}
                                aria-label={`${item?.brandNameTwo}${
                                  item?.newTabTwo
                                    ? " external link opens in a new tab"
                                    : ""
                                }`}
                                data-gbh-data-layer={`{"clickInternalLinks":"${
                                  item?.newTabTwo ? "false" : "true"
                                }","internalLinkPosition":"${
                                  data?.quadrantTitleTwo?.toLowerCase() || ""
                                }","internalLinkZoneName":"global footer","internalLinkURL":"${
                                  brandLinkTwoHref || "n/a"
                                }","internalLinkName":"${item?.brandNameTwo?.toLowerCase()}","eventName":"footer menu:${item?.brandNameTwo?.toLowerCase()}","internalLinkType":"global footer:navigation","eventType":"footer submenu","eventAction":"footer menu:${item?.brandNameTwo?.toLowerCase()}"}`}
                              >
                                {item?.brandNameTwo && item?.brandNameTwo}
                              </a>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  </>
                )}
              </div>
              <div className="col" aria-label={quadrantTitleThree}>
                {data?.descriptionThree === "descriptionThree" && (
                  <>
                    <h2>{quadrantTitleThree}</h2>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(data?.quadrantDescThree),
                      }}
                    />
                  </>
                )}
                {data?.radioNavigationThree === "linksThree" && (
                  <>
                    <div className="gbh-footer-main-nav-link">
                      {renderLinkHeading("linksThree", quadrantTitleThree)}
                      <ul
                        className={`${getLinksVisibilityClass("linksThree")} ${
                          itemsTwo.length > 4 ? "odd-even" : ""
                        }`}
                        id="footer-inner-links-linksThree"
                      >
                        {itemsTwo.map(item => (
                          <li
                            key={`${item?.brandLinkThree || ""}-${
                              item?.brandNameThree || ""
                            }`}
                          >
                            <a
                              className="gbh-data-layer"
                              href={item?.brandLinkThree
                                ?.split(
                                  process.env.NEXT_PUBLIC_INTERNALLINKPATH
                                )
                                .join("")
                                .replace(".html", "")}
                              target={item?.newTabThree ? "_blank" : "_self"}
                              rel={item?.newTabThree ? "noreferrer" : undefined}
                              aria-label={`${item?.brandNameThree}${
                                item?.newTabThree
                                  ? " external link opens in a new tab"
                                  : ""
                              }`}
                              data-gbh-data-layer={`{"clickInternalLinks":"${
                                item?.newTabThree ? "false" : "true"
                              }","internalLinkPosition":"${quadrantTitleThree.toLowerCase()}","internalLinkZoneName":"global footer","internalLinkURL":"${item?.brandLinkThree
                                ?.split(
                                  process.env.NEXT_PUBLIC_INTERNALLINKPATH
                                )
                                .join("")
                                .replace(
                                  ".html",
                                  ""
                                )}","internalLinkName":"${item?.brandNameThree?.toLowerCase()}","eventName":"footer menu:${item?.brandNameThree?.toLowerCase()}","internalLinkType":"global footer:navigation","eventType":"footer submenu","eventAction":"footer menu:${item?.brandNameThree?.toLowerCase()}"}`}
                            >
                              {item?.brandNameThree && item?.brandNameThree}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>
              <div className="col" aria-label={data?.quadrantTitleFour}>
                {data?.radioNavigationFour === "descriptionFour" && (
                  <>
                    <h2>{data?.quadrantTitleFour}</h2>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(data?.quadrantDescFour),
                      }}
                    />
                  </>
                )}
                {data?.radioNavigationFour === "linksFour" && (
                  <>
                    <div className="gbh-footer-main-nav-link">
                      {renderLinkHeading("linksFour", data?.quadrantTitleFour)}
                      <ul
                        className={`${getLinksVisibilityClass("linksFour")} ${
                          itemsThree.length > 4 ? "odd-even" : ""
                        }`}
                        id="footer-inner-links-linksFour"
                      >
                        {itemsThree.map(item => {
                          const brandLinkFourHref = item?.brandLinkFour
                            ?.split(process.env.NEXT_PUBLIC_INTERNALLINKPATH)
                            .join("")
                            .replace(".html", "")
                          const linkFourKey = `${item?.brandLinkFour || ""}-${
                            item?.brandNameFour || ""
                          }`

                          return (
                            <li key={linkFourKey}>
                              <a
                                className="gbh-data-layer"
                                href={brandLinkFourHref}
                                target={item?.newTabFour ? "_blank" : "_self"}
                                rel={
                                  item?.newTabFour ? "noreferrer" : undefined
                                }
                                aria-label={`${item?.brandNameFour}${
                                  item?.newTabFour
                                    ? " external link opens in a new tab"
                                    : ""
                                }`}
                                data-gbh-data-layer={`{"clickInternalLinks":"${
                                  item?.newTabFour ? "false" : "true"
                                }","internalLinkPosition":"${
                                  data?.quadrantTitleFour?.toLowerCase() || ""
                                }","internalLinkZoneName":"global footer","internalLinkURL":"${
                                  brandLinkFourHref || "n/a"
                                }","internalLinkName":"${item?.brandNameFour?.toLowerCase()}","eventName":"footer menu:${item?.brandNameFour?.toLowerCase()}","internalLinkType":"global footer:navigation","eventType":"footer submenu","eventAction":"footer menu:${item?.brandNameFour?.toLowerCase()}"}`}
                              >
                                {item?.brandNameFour && item?.brandNameFour}
                              </a>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        </section>
      </footer>
    </div>
  )
}

export default MainFooter

MainFooter.propTypes = {
  data: PropTypes.shape({
    itemsOne: PropTypes.arrayOf(PropTypes.object),
    itemsThree: PropTypes.arrayOf(PropTypes.object),
    quadrantTitleThree: PropTypes.string,
  }),
  columnClassNames: PropTypes.string,
}
